'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Children, Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { PIXELS } from 'DesignData/enums/SizeUnits';
import { MIN_PANE_SIZE } from 'DesignData/constants/PaneSizes';
import { HORIZONTAL, VERTICAL } from 'DesignData/enums/AxisDirections';
import PaneSize from 'DesignData/models/PaneSize';
import { updateAccordionOptions } from 'DesignData/actions/designComponents/CodeAccordion';
import { getCodemirrorOptions } from 'DesignData/selectors/codemirror';
import { getThemeClassFromThemeName } from 'react-codemirror/util/Text';
import ResizablePanes from 'DesignComponents/resizablePanes/ResizablePanes';
import Pane from 'DesignComponents/resizablePanes/Pane';
import CodeAccordionItem from 'DesignComponents/inputs/codeAccordion/CodeAccordionItem';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
const closedPaneSize = new PaneSize({
  value: MIN_PANE_SIZE,
  units: PIXELS
});

const mapStateToProps = state => {
  const {
    theme: codemirrorTheme
  } = getCodemirrorOptions(state);
  const itemOptions = state.designComponents.codeAccordion.accordionsById['settings-code-demo'] || EMPTY_OBJECT;
  return {
    codemirrorTheme,
    itemOptions
  };
};

const mapDispatchToProps = {
  updateAccordionOptions
};

function childrenPropType(props) {
  let isInvalid = false;

  if (!props.children) {
    isInvalid = true;
  } else {
    Children.forEach(props.children, child => {
      if (!(child && child.type === CodeAccordionItem)) {
        isInvalid = true;
      }
    });
  }

  return isInvalid ? new Error(`CodeAccordion expects children to be one or more ${CodeAccordionItem.displayName}`) : undefined;
}

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function getOppositeAxis(axis) {
  return axis === HORIZONTAL ? VERTICAL : HORIZONTAL;
}
/**
 * Input component for multiple CodeMirror editors displayed in accordions.
 */


class CodeAccordion extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      refresh: false
    };

    this.triggerRefresh = () => {
      this.setState({
        refresh: true
      });
      setTimeout(() => {
        this.setState({
          refresh: false
        });
      }, 0);
    };

    this.onResize = sizesById => {
      const {
        onResize,
        id
      } = this.props;
      const options = deepClone(this.props.itemOptions);
      Object.keys(sizesById).forEach(_itemId => {
        options[_itemId] = options[_itemId] || {};
        const {
          open,
          size
        } = options[_itemId];
        const _newSize = sizesById[_itemId];
        options[_itemId].size = _newSize;

        if (open !== true && size !== _newSize && !_newSize.isMinSize) {
          options[_itemId].open = true;
        } else if (open && _newSize.isMinSize) {
          options[_itemId].open = false;
        }
      });

      if (id) {
        this.props.updateAccordionOptions(id, options);
      }

      if (onResize) {
        onResize(options);
      }

      this.triggerRefresh();
    };

    this.getOnOpenChange = itemId => {
      this.__onOpenChange = this.__onOpenChange || {};

      if (!this.__onOpenChange[itemId]) {
        this.__onOpenChange[itemId] = evt => {
          const options = deepClone(this.props.itemOptions);
          options[itemId] = options[itemId] || {};
          const {
            id,
            onOpen
          } = this.props;

          if (id) {
            const {
              value: open
            } = evt.target;
            options[itemId].open = open;
            this.props.updateAccordionOptions(id, options);
          }

          if (onOpen) {
            onOpen(options);
          }

          this.triggerRefresh();
        };
      }

      return this.__onOpenChange[itemId];
    };

    this.accordionItemClassName = "code-accordion-editor m-all-0 p-all-0";
  }

  get codemirrorThemeClassName() {
    const {
      codemirrorTheme
    } = this.props;
    return getThemeClassFromThemeName(codemirrorTheme);
  }

  getPaneOptions(itemId, childProps) {
    const itemOptions = this.props.itemOptions[itemId] || {};
    let open;

    if (itemOptions.open === !!itemOptions.open) {
      open = itemOptions.open;
    } else if (childProps.open === !!childProps.open) {
      open = childProps.open;
    } else if (itemOptions.size && itemOptions.size.isMinSize) {
      open = false;
    } else {
      open = true;
    }

    const defaultSize = open === false ? closedPaneSize : itemOptions.size;
    return {
      open,
      defaultSize
    };
  }

  get accordionClassName() {
    return classNames('code-accordion-accordion', this.codemirrorThemeClassName);
  }

  renderChildren() {
    return Children.map(this.props.children, (child, idx) => {
      const itemId = child.props.id != null ? child.props.id : idx;
      const {
        open,
        defaultSize
      } = this.getPaneOptions(itemId, child.props);
      const {
        codemirror
      } = child.props;
      return /*#__PURE__*/_jsx(Pane, {
        className: "code-accordion-pane",
        defaultSize: defaultSize,
        size: child.props.size,
        paneId: itemId,
        direction: this.props.direction,
        children: /*#__PURE__*/_jsx(UIAccordionItem, {
          animate: false,
          className: this.accordionClassName,
          contentClassName: this.accordionItemClassName,
          title: /*#__PURE__*/_jsx("span", {
            className: "code-accordion-item-title",
            children: child.props.title
          }),
          open: open,
          onOpenChange: this.getOnOpenChange(itemId),
          children: /*#__PURE__*/_jsx(codemirror.type, Object.assign({}, codemirror.props, {
            width: "100%",
            height: "100%"
          }))
        })
      }, idx);
    });
  }

  renderAlert() {
    const {
      alert
    } = this.props;
    if (!alert) return null;
    return /*#__PURE__*/_jsx("div", {
      className: "code-accordion-alert",
      children: alert
    });
  }

  render() {
    return /*#__PURE__*/_jsxs(UIFlex, {
      className: "design-components-code-accordion",
      direction: "column",
      children: [this.renderAlert(), /*#__PURE__*/_jsx(UIBox, {
        className: "code-accordion-container",
        grow: 1,
        children: /*#__PURE__*/_jsx(ResizablePanes, {
          className: "code-accordion-items",
          onPaneResize: this.onResize,
          paneMinSize: MIN_PANE_SIZE,
          direction: getOppositeAxis(this.props.direction),
          children: this.renderChildren()
        })
      })]
    });
  }

}

CodeAccordion.propTypes = {
  /**
   * @type {Number|String} Used to store/fetch accordion settings.
   *                       If not specified settings will not be stored.
   */
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * @type {Node<CodeAccordionItem>} One or more CodeAccordionItem components.
   */
  children: childrenPropType,

  /**
   * @type {AxisDirections}
   */
  direction: PropTypes.oneOf([HORIZONTAL, VERTICAL]),

  /**
   * @type {Function} Optional callback that fires on accordion resize events.
   */
  onResize: PropTypes.func,

  /**
   * @type {Function} Optional callback that fires on accordion open/close events.
   */
  onOpen: PropTypes.func,

  /**
   * @type {Node} Optional alert component to be displayed.
   */
  alert: PropTypes.node,
  // Connected fields
  itemOptions: PropTypes.object.isRequired,
  updateAccordionOptions: PropTypes.func.isRequired,
  codemirrorTheme: PropTypes.string.isRequired
};
CodeAccordion.defaultProps = {
  direction: VERTICAL
};
export default connect(mapStateToProps, mapDispatchToProps)(CodeAccordion);